import type { PermissionModule } from '@polatkan/api/permissions';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Types
export type AppType = 'TRAVEL' | 'TRADING';
export type PermissionAction =
  | 'VIEW'
  | 'CREATE'
  | 'EDIT'
  | 'DELETE'
  | 'EXPORT'
  | 'APPROVE';

export interface AppAccess {
  app: AppType;
  isDefault: boolean;
}

export interface Permissions {
  apps: AppAccess[];
  modules: {
    TRAVEL: Record<string, PermissionAction[]>;
    TRADING: Record<string, PermissionAction[]>;
  };
  isSuperAdmin: boolean;
}

interface AppState {
  // Current app context
  currentApp: AppType | null;

  // User permissions
  permissions: Permissions | null;

  // Actions
  setCurrentApp: (app: AppType) => void;
  setPermissions: (permissions: Permissions) => void;
  clearAppState: () => void;

  // Permission helpers
  hasAppAccess: (app: AppType) => boolean;
  hasPermission: (module: PermissionModule, action?: PermissionAction) => boolean;
  hasPermissionForApp: (
    app: AppType,
    module: PermissionModule,
    action?: PermissionAction
  ) => boolean;
  canAccessBothApps: () => boolean;
  getDefaultApp: () => AppType | null;
  getAccessibleApps: () => AppType[];
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      currentApp: null,
      permissions: null,

      setCurrentApp: (app) => set({ currentApp: app }),

      setPermissions: (permissions) => {
        const state = get();
        // `currentApp` is persisted to localStorage, so it can outlive the user
        // who chose it — the next person to log in on this browser inherits it.
        // Keep it only while the incoming permissions still grant that app;
        // otherwise fall back to their default, or the store would hand them a
        // sidebar filtered to an app they cannot see and no way to switch back.
        const accessible = permissions.isSuperAdmin
          ? (['TRAVEL', 'TRADING'] as AppType[])
          : permissions.apps.map((a) => a.app);

        const keepCurrent =
          state.currentApp !== null && accessible.includes(state.currentApp);

        const fallback = permissions.isSuperAdmin
          ? 'TRAVEL'
          : (permissions.apps.find((a) => a.isDefault)?.app ??
            permissions.apps[0]?.app ??
            null);

        set({
          permissions,
          currentApp: keepCurrent ? state.currentApp : fallback,
        });
      },

      clearAppState: () =>
        set({
          currentApp: null,
          permissions: null,
        }),

      hasAppAccess: (app) => {
        const { permissions } = get();
        if (!permissions) return false;
        if (permissions.isSuperAdmin) return true;
        return permissions.apps.some((a) => a.app === app);
      },

      hasPermission: (module, action) => {
        const { currentApp, permissions } = get();
        if (!permissions || !currentApp) return false;
        if (permissions.isSuperAdmin) return true;

        const modulePerms = permissions.modules[currentApp]?.[module];
        if (!modulePerms || modulePerms.length === 0) return false;

        // If no specific action requested, check if user has any permission
        if (!action) return true;

        return modulePerms.includes(action);
      },

      hasPermissionForApp: (app, module, action) => {
        const { permissions } = get();
        if (!permissions) return false;
        if (permissions.isSuperAdmin) return true;

        // Check if user has access to the app
        if (!permissions.apps.some((a) => a.app === app)) return false;

        const modulePerms = permissions.modules[app]?.[module];
        if (!modulePerms || modulePerms.length === 0) return false;

        // If no specific action requested, check if user has any permission
        if (!action) return true;

        return modulePerms.includes(action);
      },

      canAccessBothApps: () => {
        const { permissions } = get();
        if (!permissions) return false;
        if (permissions.isSuperAdmin) return true;
        return permissions.apps.length >= 2;
      },

      getDefaultApp: () => {
        const { permissions } = get();
        if (!permissions) return null;
        if (permissions.isSuperAdmin) return 'TRAVEL';
        const defaultApp = permissions.apps.find((a) => a.isDefault);
        return defaultApp?.app || permissions.apps[0]?.app || null;
      },

      getAccessibleApps: () => {
        const { permissions } = get();
        if (!permissions) return [];
        if (permissions.isSuperAdmin) return ['TRAVEL', 'TRADING'];
        return permissions.apps.map((a) => a.app);
      },
    }),
    {
      name: 'polatkan-app',
      partialize: (state) => ({
        currentApp: state.currentApp,
        permissions: state.permissions,
      }),
    }
  )
);

// Share the browser-safe registry with the API and role templates.
export { TRAVEL_MODULES, TRADING_MODULES, PERMISSION_ACTIONS, TRAVEL_MODULE_LABELS, TRADING_MODULE_LABELS, PERMISSION_ACTION_LABELS } from '@polatkan/api/permissions';
export type { PermissionModule } from '@polatkan/api/permissions';
